/**
 * What changed since the draft before (plan v21 B4): a reviewer who sent a draft back reads first what they asked
 * for, then what the author changed, so they judge the new draft against their note rather than reading it all again.
 * Kept pure, from the two versions' stored JSON (manifest, runs, author_notes), read as the review reads them. It
 * never claims more than it compared: each stream by its parameters, units, mode and equipment keys; counts only
 * between runs of one kind; questions by what they ask. "Nothing changed" names what was compared.
 */
import type { ImportExtractorVersion } from "@/lib/api/imports";
import { defectLabel } from "@/lib/lot-words";
import { streamEquipmentKeys } from "./hub";
import { own } from "./own";
import { equipmentProposalsOf } from "./proposal";
import {
  draftSummary,
  fmtCount,
  manifestStreams,
  proposedWordsOf,
  questionGroups,
  runSources,
  vocabKey,
  type DraftStream,
  type StreamSummary,
} from "./review";

/** The version a draft is read against: the latest one before it, whatever its status (sent back, in use, retired). */
export function previousVersionOf<V extends Pick<ImportExtractorVersion, "version">>(
  versions: readonly V[],
  current: Pick<ImportExtractorVersion, "version">,
): V | null {
  let best: V | null = null;
  for (const v of versions) if (v.version < current.version && (!best || v.version > best.version)) best = v;
  return best;
}

/** The section's head: which version, what it is, and the note it was sent back with. The extractor's list of
 *  versions carries all three, so it shows before the version itself loads. */
export interface SinceHeader {
  previous: number;
  /** "sent back", "in use", "retired" or "a draft". */
  previousState: string;
  /** The note the version before was sent back with ("" when it was not sent back). */
  sentBackWith: string;
}

const STATE_TEXT: Record<ImportExtractorVersion["status"], string> = {
  rejected: "sent back",
  active: "in use",
  retired: "retired",
  draft: "a draft",
};

export function sinceHeader(previous: Pick<ImportExtractorVersion, "version" | "status" | "review_note">): SinceHeader {
  return {
    previous: previous.version,
    previousState: own(STATE_TEXT, previous.status) ?? previous.status,
    sentBackWith: previous.status === "rejected" ? (previous.review_note ?? "").trim() : "",
  };
}

export interface SinceChanges {
  /** What changed, a sentence each. */
  changes: string[];
  /** What was compared, in words, for the sentence that says nothing changed in it. */
  compared: string[];
  /** Why the counts were not compared ("" when they were). */
  countsNote: string;
}

/** "a, b and c". */
function listText(items: readonly string[]): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

const quoted = (items: readonly string[]) => items.map((s) => `"${s}"`);
const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/** What a stream imports, as one count: its rows, else its lots. */
function streamCount(s: StreamSummary): number | null {
  return s.rows ?? s.lots;
}

/** "Set aside: 3, was 9.", or nothing when equal or unknown on either side. */
function countChange(label: string, before: number | null, after: number | null): string | null {
  if (before === null || after === null || before === after) return null;
  return `${label}: ${fmtCount(after)}, was ${fmtCount(before)}.`;
}

/** What a word means, for the reader: its defect's name, or the plant's own category. */
function meaning(taxonomyId: string | null): string {
  return taxonomyId ? defectLabel(taxonomyId).toLowerCase() : "the plant's own category";
}

/** The equipment keys one manifest stream binds, as the approval reads them (hub streamEquipmentKeys). */
function equipmentKeysOf(s: DraftStream): string[] {
  return streamEquipmentKeys({ key: s.key, mode: s.mode, equipment: s.equipment, params: s.params });
}

/** What changed inside one stream both versions have: its mode, parameters, units and equipment keys. */
function streamContentChanges(now: DraftStream, was: DraftStream): string[] {
  const out: string[] = [];
  const at = `In ${now.key}`;
  if (now.mode !== was.mode) out.push(`${at}, the stream is now ${now.mode || "of no mode"}, was ${was.mode || "of no mode"}.`);
  const nowTags = new Map(now.params.map((p) => [p.tag, p]));
  const wasTags = new Map(was.params.map((p) => [p.tag, p]));
  const added = now.params.filter((p) => !wasTags.has(p.tag)).map((p) => p.tag);
  const gone = was.params.filter((p) => !nowTags.has(p.tag)).map((p) => p.tag);
  if (added.length) out.push(`${at}, new ${plural(added.length, "parameter", "parameters")}: ${listText(added)}.`);
  if (gone.length) out.push(`${at}, ${plural(gone.length, "a parameter", "parameters")} no longer read: ${listText(gone)}.`);
  for (const p of now.params) {
    const before = wasTags.get(p.tag);
    if (before && before.unit !== p.unit) out.push(`${at}, ${p.tag} is now in ${p.unit || "no unit"}, was ${before.unit || "no unit"}.`);
    if (before && before.source !== p.source) out.push(`${at}, ${p.tag} is now read from "${p.source}", was "${before.source}".`);
  }
  const nowEq = equipmentKeysOf(now);
  const wasEq = equipmentKeysOf(was);
  if (nowEq.join("\n") !== wasEq.join("\n")) {
    out.push(`${at}, the equipment ${plural(nowEq.length, "key is", "keys are")} now ${listText(nowEq) || "none"}, was ${listText(wasEq) || "none"}.`);
  }
  return out;
}

/** A question as itself: one sentence, or its four parts, compared case-blind. */
function questionKey(q: { text: string; saw: string; assume: string; changes: string }): string {
  return (q.text || [q.saw, q.assume, q.changes].join(" | ")).trim().toLowerCase();
}

/** "nothing changed" in words: only what was compared. */
export function nothingChangedText(previous: number, c: Pick<SinceChanges, "compared">): string {
  return `Nothing changed since v${previous} in the ${listText(c.compared)}.`;
}

/** What changed between the version before and this one. `names` gives the plant items' names by id. */
export function sinceChanges(
  current: ImportExtractorVersion,
  previous: ImportExtractorVersion,
  names: Readonly<Record<string, string>>,
): SinceChanges {
  const changes: string[] = [];
  const compared: string[] = ["streams and their parameters"];

  // The streams, by the manifest: added, dropped, and what changed inside each one both have.
  const nowStreams = manifestStreams(current.manifest);
  const wasStreams = manifestStreams(previous.manifest);
  const wasByKey = new Map(wasStreams.map((s) => [s.key, s]));
  const nowKeys = new Set(nowStreams.map((s) => s.key));
  const added = nowStreams.filter((s) => !wasByKey.has(s.key)).map((s) => s.key);
  const dropped = wasStreams.filter((s) => !nowKeys.has(s.key)).map((s) => s.key);
  if (added.length) changes.push(`New ${plural(added.length, "stream", "streams")}: ${listText(added)}.`);
  if (dropped.length) changes.push(`Dropped ${plural(dropped.length, "stream", "streams")}: ${listText(dropped)}.`);
  for (const s of nowStreams) {
    const before = wasByKey.get(s.key);
    if (before) changes.push(...streamContentChanges(s, before));
  }

  // The counts, only when each half of both runs (the counts per stream, and what was set aside and skipped) comes
  // from the same kind of run: a self-test reads a sample, a dry run the whole file.
  const nowRun = runSources(current);
  const wasRun = runSources(previous);
  let countsNote = "";
  if (!nowRun.preview && !nowRun.report) countsNote = "This draft has no counts yet, so they are not compared.";
  else if (!wasRun.preview && !wasRun.report) countsNote = `v${previous.version} had no counts, so they are not compared.`;
  else if (nowRun.preview !== wasRun.preview || nowRun.report !== wasRun.report) {
    countsNote =
      `This draft's counts and v${previous.version}'s come from different runs (a self-test reads a sample of the file, ` +
      "a dry run all of it), so they are not compared.";
  } else {
    compared.push("counts");
    const now = draftSummary(current);
    const was = draftSummary(previous);
    for (const s of now.streams) {
      const before = was.streams.find((w) => w.key === s.key);
      const line = before ? countChange(s.key, streamCount(before), streamCount(s)) : null;
      if (line) changes.push(line);
    }
    const setAside = countChange("Set aside", was.setAside, now.setAside);
    if (setAside) changes.push(setAside);
    const skipped = countChange("Skipped on purpose", was.skippedTotal, now.skippedTotal);
    if (skipped) changes.push(skipped);
  }

  // The defect words, by the key the server binds them on.
  compared.push("defect words");
  const nowWords = proposedWordsOf(current.author_notes);
  const wasWords = proposedWordsOf(previous.author_notes);
  const wasWord = new Map(wasWords.map((w) => [vocabKey(w.word), w]));
  const nowWord = new Map(nowWords.map((w) => [vocabKey(w.word), w]));
  const newWords = nowWords.filter((w) => !wasWord.has(vocabKey(w.word))).map((w) => w.word);
  const goneWords = wasWords.filter((w) => !nowWord.has(vocabKey(w.word))).map((w) => w.word);
  if (newWords.length) changes.push(`New defect ${plural(newWords.length, "word", "words")}: ${listText(quoted(newWords))}.`);
  if (goneWords.length) changes.push(`Defect ${plural(goneWords.length, "word", "words")} no longer proposed: ${listText(quoted(goneWords))}.`);
  for (const w of nowWords) {
    const before = wasWord.get(vocabKey(w.word));
    if (before && before.taxonomyId !== w.taxonomyId) {
      changes.push(`"${w.word}" now means ${meaning(w.taxonomyId)}, was ${meaning(before.taxonomyId)}.`);
    }
  }

  // The equipment the author proposed, by key: first proposed, changed, and dropped.
  compared.push("equipment the brain proposed");
  const nowEq = equipmentProposalsOf(current.author_notes);
  const wasEq = equipmentProposalsOf(previous.author_notes);
  const itemText = (id: string | null) => (id === null ? "no item (none fits)" : own(names, id) || "an item not in the list");
  const nowRoles = Object.keys(nowEq);
  const wasRoles = Object.keys(wasEq);
  if (wasRoles.length === 0 && nowRoles.length > 0) {
    changes.push(`The brain now proposes the equipment (v${previous.version} proposed none): see each stream below.`);
  } else {
    for (const role of nowRoles) {
      const p = nowEq[role];
      const before = own(wasEq, role);
      if (!before) changes.push(`For ${role}, the brain now proposes ${itemText(p.entityId)}.`);
      else if (before.entityId !== p.entityId) {
        changes.push(`For ${role}, the brain now proposes ${itemText(p.entityId)}, was ${itemText(before.entityId)}.`);
      }
    }
    for (const role of wasRoles) {
      if (!own(nowEq, role)) changes.push(`For ${role}, the brain proposes nothing now, was ${itemText(wasEq[role].entityId)}.`);
    }
  }

  // The questions, by what each asks.
  compared.push("questions");
  const asked = (notes: unknown) => new Set(questionGroups(notes).flatMap((g) => g.questions.map(questionKey)));
  const nowQ = asked(current.author_notes);
  const wasQ = asked(previous.author_notes);
  const newQ = [...nowQ].filter((q) => !wasQ.has(q)).length;
  const goneQ = [...wasQ].filter((q) => !nowQ.has(q)).length;
  if (newQ) changes.push(`${fmtCount(newQ)} new ${plural(newQ, "question", "questions")}.`);
  if (goneQ) changes.push(`${fmtCount(goneQ)} ${plural(goneQ, "question", "questions")} no longer asked.`);

  return { changes, compared, countsNote };
}
